import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { User, Users } from 'lucide-react';
import { QuestionnaireData } from '@/types/category';

interface PersonalInfoStepProps {
  formData: Partial<QuestionnaireData>;
  onUpdate: (updates: Partial<QuestionnaireData>) => void;
  sessionType: 'individual' | 'group';
  isNameDisabled?: boolean;
  groupName?: string;
}

export const PersonalInfoStep: React.FC<PersonalInfoStepProps> = ({
  formData,
  onUpdate,
  sessionType,
  isNameDisabled = false,
  groupName
}) => {
  const handleInputChange = (field: keyof QuestionnaireData, value: string) => {
    onUpdate({ [field]: value });
  };

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-3">
          {sessionType === 'individual' ? (
            <User className="h-6 w-6 text-primary" />
          ) : (
            <Users className="h-6 w-6 text-primary" />
          )}
        </div>
        <h3 className="text-lg font-semibold mb-1">
          {sessionType === 'individual' ? 'Personal Information' : 'Group Information'}
        </h3>
        <p className="text-sm text-muted-foreground">
          {sessionType === 'individual'
            ? "Let's get to know you better for a more personalized experience"
            : "Tell us about your group for a tailored session"
          }
        </p>
      </div>

      {/* Individual Session Fields */}
      {sessionType === 'individual' && (
        <>
          {/* Name Fields */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-sm font-medium">
                    First Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="firstName"
                    placeholder="Enter your first name"
                    value={formData.firstName || ''}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    disabled={isNameDisabled}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-sm font-medium">
                    Last Name
                  </Label>
                  <Input
                    id="lastName"
                    placeholder="Enter your last name"
                    value={formData.lastName || ''}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    disabled={isNameDisabled}
                    className="rounded-xl"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Personal Info */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="age" className="text-sm font-medium">
                    Age
                  </Label>
                  <Input
                    id="age"
                    placeholder="Your age"
                    value={formData.age || ''}
                    onChange={(e) => handleInputChange('age', e.target.value)}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="occupation" className="text-sm font-medium">
                    Occupation
                  </Label>
                  <Input
                    id="occupation"
                    placeholder="Your profession"
                    value={formData.occupation || ''}
                    onChange={(e) => handleInputChange('occupation', e.target.value)}
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location" className="text-sm font-medium">
                  Location
                </Label>
                <Input
                  id="location"
                  placeholder="City, State or Country"
                  value={formData.location || ''}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="hobbies" className="text-sm font-medium">
                  Hobbies & Interests
                </Label>
                <Textarea
                  id="hobbies"
                  placeholder="What do you enjoy doing in your free time?"
                  value={formData.hobbies || ''}
                  onChange={(e) => handleInputChange('hobbies', e.target.value)}
                  className="min-h-[80px] resize-none rounded-xl"
                />
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Group Session Fields */}
      {sessionType === 'group' && (
        <>
          <Card>
            <CardContent className="pt-6 space-y-4">
              {/* If group is already selected (via GroupSessionSelector), show as read-only */}
              {groupName ? (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Group
                  </Label>
                  <div className="p-3 bg-muted rounded-xl text-sm font-medium">
                    {groupName}
                  </div>
                </div>
              ) : (
                // No group selected yet - show editable group name input
                <div className="space-y-2">
                  <Label htmlFor="groupName" className="text-sm font-medium">
                    Group Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="groupName"
                    placeholder="Enter your group's name"
                    value={formData.groupName || ''}
                    onChange={(e) => handleInputChange('groupName', e.target.value)}
                    className="rounded-xl"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="groupDescription" className="text-sm font-medium">
                  Group Description
                </Label>
                <Textarea
                  id="groupDescription"
                  placeholder="Tell us about your group (e.g., team members, purpose, dynamics)"
                  value={formData.groupDescription || ''}
                  onChange={(e) => handleInputChange('groupDescription', e.target.value)}
                  className="min-h-[100px] resize-none rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="groupSize" className="text-sm font-medium">
                  Group Size
                </Label>
                <Input
                  id="groupSize"
                  type="number"
                  placeholder="Number of participants"
                  value={formData.groupSize || ''}
                  onChange={(e) => handleInputChange('groupSize', e.target.value)}
                  className="rounded-xl"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="groupGoals" className="text-sm font-medium">
                  Group Goals
                </Label>
                <Textarea
                  id="groupGoals"
                  placeholder="What does your group hope to achieve from this session?"
                  value={formData.groupGoals || ''}
                  onChange={(e) => handleInputChange('groupGoals', e.target.value)}
                  className="min-h-[80px] resize-none rounded-xl"
                />
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};